import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /** @type {boolean} */
  #metaCartSyncInFlight = false;

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (this.#isAndroidMetaBrowser() && this.#isBackForwardRestore(event)) {
      this.#syncCartFromServer();
      return;
    }

    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Checks whether the current browser is Meta's in-app browser on Android.
   * @returns {boolean}
   */
  #isAndroidMetaBrowser() {
    const userAgent = navigator.userAgent || '';
    return /Android/i.test(userAgent) && /(Instagram|FBAN|FBAV)/i.test(userAgent);
  }

  /**
   * Checks whether pageshow represents a back/forward restore.
   * @param {PageTransitionEvent} event
   * @returns {boolean}
   */
  #isBackForwardRestore(event) {
    if (event.persisted) return true;

    const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
    if (navigationEntry && 'type' in navigationEntry && navigationEntry.type === 'back_forward') return true;

    // Older Android WebViews may only expose the legacy Navigation Timing API.
    return performance.navigation?.type === 2;
  }

  /**
   * Reconciles the visible cart with Shopify after Meta WebView restores an old page.
   * No full-page navigation or reload is used.
   */
  async #syncCartFromServer() {
    if (this.#metaCartSyncInFlight) return;
    this.#metaCartSyncInFlight = true;

    try {
      // Do not let a recent sessionStorage value overwrite the server-rendered count.
      sessionStorage.removeItem('cart-count');

      const syncId = Date.now().toString();
      const cartUrl = new URL(`${Theme.routes.cart_url}.js`, window.location.origin);
      cartUrl.searchParams.set('_gos_cart_sync', syncId);

      const cartResponse = await fetch(cartUrl, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!cartResponse.ok) {
        throw new Error(`Cart sync failed with status ${cartResponse.status}`);
      }

      const cart = await cartResponse.json();
      const sectionIds = new Set();

      document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
        const sectionId = component.getAttribute('data-section-id');
        if (sectionId) sectionIds.add(sectionId);
      });

      const renderUrl = new URL(window.location.href);
      renderUrl.searchParams.set('_gos_cart_sync', syncId);

      const sections = {};
      await Promise.all(
        Array.from(sectionIds).map(async (sectionId) => {
          sections[sectionId] = await sectionRenderer.getSectionHTML(sectionId, false, new URL(renderUrl));
        })
      );

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'gos-meta-return-sync', {
          itemCount: Number(cart.item_count) || 0,
          source: 'gos-meta-return-sync',
          sections,
        })
      );
    } catch (error) {
      console.error('GoldOtoSepet cart return sync failed:', error);
      this.ensureCartBubbleIsCorrect();
    } finally {
      this.#metaCartSyncInFlight = false;
    }
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
